import { useContext, useEffect, useState } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useAreasApi, AreaItem, AreaDto } from "../../../services/api/areasApi";
import { AreaItemGeneric } from "../../../components/types/generic.types";
import AreaDataTable from "../../../components/module/admin/GenericDataTable/AreaDataTable";
import AreasModal from "../../../components/module/admin/AreasModal/AreasModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Areas = () => {
  const [items, setItems] = useState<AreaItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AreaItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useAreasApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 90 },
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 200 },
    { field: "Ceco", headerName: "CECO", flex: 1, minWidth: 150 },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams<AreaItemGeneric>) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AreaItemGeneric>) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: AreaItemGeneric) => {
    const areaItem: AreaItem = {
      Id: item.Id,
      Nombre: item.Nombre,
      Ceco: item.Ceco || "",
      Activo: item.Activo,
      Eliminado: item.Eliminado,
    };
    setEditingItem(areaItem);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará el área y podría afectar los cargos asociados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const success = await remove(id);
        if (success) {
          await loadItems();
          toast.success("El área ha sido eliminada.");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este área?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: AreaDto = {
            Nombre: item.Nombre,
            Ceco: item.Ceco,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del área ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: AreaDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("No se pudo guardar el registro.");
    }
  };

  return (
    <PageLayout icon={LocationOnIcon} title="Áreas">
        <div style={{ padding: "24px" }}>
          <AreaDataTable
            items={items}
            columns={columns}           
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <AreasModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Área" : "Nueva Área"}
            initialData={
              editingItem
                ? {
                    Nombre: editingItem.Nombre,
                    Ceco: editingItem.Ceco,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Areas;